import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Search } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Checkbox } from "../../../../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/ui/table";
import { useOptroWorkpaperMatchJob } from "../../../../hooks/useOptroWorkpaperMatchJob";
import { useOptroLinkedWorkpapers } from "../../../../hooks/useOptroLinkedWorkpapers";
import { useOptroImport } from "../../../../contexts/optroImportContext";
import { OptroWorkpaperFile } from "../../../../models/audit/optroSync";
import { useOptroImportWizard } from "./optroImportWizardContext";
import { WorkpaperChip } from "./components/WorkpaperChip";
import { MatchingWorkpaperIndicator } from "./components/MatchingWorkpaperIndicator";
import { ChangeWorkpaperDialog } from "./components/ChangeWorkpaperDialog";
import { FilterCombobox } from "./components/FilterCombobox";

export const OptroImportReviewPage = () => {
  const navigate = useNavigate();
  const {
    selected,
    clearSelection,
    workpaperDecisions,
    setWorkpapers,
    userEditedRefs,
    linkedWorkpapersCache,
  } = useOptroImportWizard();
  const { startImport } = useOptroImport();

  // Set the moment an import is kicked off, so the empty-selection guard
  // below does not fire when `clearSelection()` empties the wizard on the way
  // out. Without it the guard's `replace` navigation wins the race against
  // the deliberate navigation to the controls list, and the user lands back
  // on the import start page instead of the list where the new rows appear.
  // Mirrors OptroWorkStepImportReviewPage.
  const leavingAfterImportRef = useRef(false);

  // Deep-linking into review without selections isn't meaningful.
  useEffect(() => {
    if (selected.length === 0 && !leavingAfterImportRef.current) {
      navigate("/audit/controls/v2/import/optro", { replace: true });
    }
  }, [selected.length, navigate]);

  const refs = useMemo(
    () => selected.map((control) => control.externalRef),
    [selected],
  );
  const { results } = useOptroWorkpaperMatchJob(refs);

  // Seed decisions as each ref reaches a terminal status (user edits win
  // afterwards). Refs still queued/crawling are skipped — seeding from their
  // placeholder empty `workpapers` would prematurely mark the control as
  // "no workpaper found" before the real result lands.
  useEffect(() => {
    Object.entries(results).forEach(([ref, result]) => {
      if (result.status === "queued" || result.status === "crawling") return;
      if (!(ref in workpaperDecisions)) {
        setWorkpapers(ref, result.workpapers, "match");
      }
    });
    // Seed once per results update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  const [search, setSearch] = useState("");
  // Process names present in the selection (client-side filter; null = all).
  const [processFilter, setProcessFilter] = useState<string | null>(null);
  const [noWorkpaperOnly, setNoWorkpaperOnly] = useState(false);
  const [dialogRef, setDialogRef] = useState<string | null>(null);

  const processOptions = useMemo(
    () =>
      Array.from(
        new Set(
          selected
            .map((c) => c.process)
            .filter((process): process is string => process !== null),
        ),
      )
        .sort()
        .map((name) => ({ id: name, name })),
    [selected],
  );

  const workpapersFor = (ref: string): OptroWorkpaperFile[] =>
    workpaperDecisions[ref] ?? [];

  // A row is "pending" until its match job status is terminal — its ref
  // isn't seeded into workpaperDecisions yet (see the seeding effect above),
  // so counts/filters below must not treat "not yet decided" the same as
  // "decided: no workpaper".
  const isPending = (ref: string): boolean => {
    const status = results[ref]?.status ?? "queued";
    return status === "queued" || status === "crawling";
  };

  const hasNoWorkpaper = (ref: string): boolean =>
    ref in workpaperDecisions && workpaperDecisions[ref].length === 0;

  // `failed` means the match job produced no answer, whether the crawl failed,
  // the run ended unexpectedly, or its result was lost. It only affects the
  // chip label; user decisions take precedence over the stale match status.
  const isMatchFailed = (ref: string): boolean =>
    !userEditedRefs.has(ref) && results[ref]?.status === "failed";

  const noWorkpaperCount = selected.filter((c) =>
    hasNoWorkpaper(c.externalRef),
  ).length;

  const visible = selected.filter((control) => {
    const term = search.trim().toLowerCase();
    if (
      term &&
      !control.name.toLowerCase().includes(term) &&
      !control.externalRef.toLowerCase().includes(term) &&
      !(control.process ?? "").toLowerCase().includes(term)
    ) {
      return false;
    }
    if (processFilter !== null && control.process !== processFilter) {
      return false;
    }
    if (
      noWorkpaperOnly &&
      !isPending(control.externalRef) &&
      !hasNoWorkpaper(control.externalRef)
    ) {
      return false;
    }
    return true;
  });

  const onContinue = () => {
    startImport(
      selected.map((control) => ({
        control,
        workpapers: workpapersFor(control.externalRef),
      })),
    );
    leavingAfterImportRef.current = true;
    navigate("/audit/controls/v2");
    clearSelection();
  };

  const dialogControl = selected.find((c) => c.externalRef === dialogRef);

  // Every workpaper linked to the control the dialog is open on. Fetched here
  // rather than inside ChangeWorkpaperDialog so the same dialog can serve the
  // work-step wizard, which resolves its candidates from a different endpoint.
  const {
    workpapers: dialogCandidates,
    loading: dialogCandidatesLoading,
    refreshing: dialogCandidatesRefreshing,
    refresh: refreshDialogCandidates,
  } = useOptroLinkedWorkpapers(
    linkedWorkpapersCache,
    dialogRef,
    dialogRef !== null,
  );

  return (
    <div className="h-screen flex flex-col w-full p-6 max-w-[1200px] mx-auto">
      <nav className="text-sm text-stone-500 mb-4">
        <Link to="/audit/controls/v2" className="hover:underline">
          Controls
        </Link>
        {" › "}
        <Link to="/audit/controls/v2/import/optro" className="hover:underline">
          Import from Optro
        </Link>
        {" › "}
        <span className="text-stone-900">Review &amp; Configure</span>
      </nav>

      <h1 className="text-2xl font-semibold">
        Review &amp; Configure Workpapers
      </h1>
      <p className="text-sm text-stone-500 mt-1 mb-4">
        Confirm the workpaper used to configure each control. Change the period,
        upload your own, or skip to configure from control metadata only.
      </p>

      {noWorkpaperCount > 0 && (
        <div className="flex items-start gap-2 border border-amber-200 bg-amber-50 rounded-md px-4 py-3 mb-4 text-sm">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
          <span>
            <span className="font-medium">
              {noWorkpaperCount} of {selected.length} controls have no workpaper
              on file.
            </span>{" "}
            <span className="text-stone-600">
              These will be configured from control metadata only. You can add
              documents later.
            </span>
          </span>
        </div>
      )}

      <div className="shrink-0 flex items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or process..."
            className="pl-9"
          />
        </div>
        <FilterCombobox
          options={processOptions}
          value={processFilter}
          onChange={setProcessFilter}
          allLabel="All processes"
          searchPlaceholder="Search processes..."
          emptyMessage="No processes found."
        />
        <label className="flex items-center gap-2 text-sm whitespace-nowrap cursor-pointer">
          <Checkbox
            checked={noWorkpaperOnly}
            onCheckedChange={(v) => setNoWorkpaperOnly(v === true)}
          />
          No workpaper only
        </label>
      </div>

      {/* The table is the page's only scroll region; the action bar below
          stays pinned on screen regardless of row count. */}
      <div className="flex-1 min-h-0 border rounded-lg overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Control</TableHead>
              <TableHead>Process</TableHead>
              <TableHead>Workpaper(s)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((control) => (
              <TableRow key={control.externalRef}>
                <TableCell className="font-medium">
                  {control.idCode ?? control.externalRef} - {control.name}
                </TableCell>
                <TableCell className="text-stone-600">
                  {control.process ?? "–"}
                </TableCell>
                <TableCell>
                  {isPending(control.externalRef) ? (
                    <MatchingWorkpaperIndicator />
                  ) : (
                    <WorkpaperChip
                      workpapers={workpapersFor(control.externalRef)}
                      matchFailed={isMatchFailed(control.externalRef)}
                      onChange={() => setDialogRef(control.externalRef)}
                      onRemove={() =>
                        setWorkpapers(control.externalRef, [], "user")
                      }
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="shrink-0 flex items-center justify-between pt-4">
        <span className="text-sm text-stone-600">
          {selected.length} control
          {selected.length === 1 ? "" : "s"} to configure
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/audit/controls/v2/import/optro")}
          >
            Go Back
          </Button>
          <Button onClick={onContinue}>Continue</Button>
        </div>
      </div>

      <ChangeWorkpaperDialog
        open={dialogRef !== null}
        onOpenChange={(open) => {
          if (!open) setDialogRef(null);
        }}
        targetKey={dialogRef}
        candidates={dialogCandidates}
        candidatesLoading={dialogCandidatesLoading}
        title="Change Workpaper"
        description="Add the workpaper(s) used to configure this control, from Optro's linked workpapers or your own uploads."
        emptyMessage="No workpapers yet. Add one below."
        current={dialogControl ? workpapersFor(dialogControl.externalRef) : []}
        onApply={(files) => {
          if (dialogRef) setWorkpapers(dialogRef, files, "user");
        }}
        onRefresh={refreshDialogCandidates}
        refreshing={dialogCandidatesRefreshing}
      />
    </div>
  );
};
